// Payload builders for the paginated Market Overview endpoint.
//
// The filter panel keeps every selection in one flat array of prefixed leaf ids
// ("prt_Apartment", "msr_Now Selling", "ccd_Q3 2026", …). decomposeFilterArray()
// is the single place that unpacks them: it feeds both MarketOverview's
// client-side predicate (buildFilterPredicate, used while pagination is local)
// and the remote payload built here, so the two paths cannot drift into
// filtering differently.
//
// Nothing in this module touches Vue reactivity — these are plain functions over
// plain values, so they stay testable and reusable from either path.

// Statuses matched exactly rather than by substring: "Now Selling" must not also
// match the admin-only "Now Selling 2". Every other status is a substring match.
// Sent with the payload so the server can apply the same rule.
export const EXACT_STATUS_MATCH = [
  "now selling",
  "sold out",
  "now leasing",
  "leased out",
];

// City and neighbourhood selections are OR'd against each other, never AND'd —
// a row passes if either set matches (see buildFilterPredicate). Stated on the
// wire so the backend can't reasonably guess wrong.
export const LOCATION_MATCH = "or";

// Table column → the field the server sorts on, for columns whose cell value is
// not the sortable value. `customProjectName` packs "name|||developer|||link"
// and sorts on the name alone; the photo column sorts on how many photos exist;
// "Type & Storey" is a composed display string.
//
// The massing columns are the other case: their cell value is read out of the
// row's `unit_collection` block, which the server attaches only to the page it
// is about to return — after the sort has already run. They sort on the flat
// row key carrying the same UTC field instead, each of which
// presaledb_mo_number_fields() already treats as numeric.
export const SORT_FIELD_ALIASES = {
  customProjectName: "name",
  customTypeStoreyData: "project_type",
  customConstructionStatus: "construction_status",
  field_construction_photos: "construction_photo_count",
  // Both photo columns are derived from construction_photo_timeline_result and
  // exist on the row under a name of their own server-side (presaledb_mo_derive),
  // so the sort has to ask for that name — the column's own field is nested in
  // the timeline and is null on every row at the top level.
  field_construction_photo_date: "construction_photo_date",
  total_parking: "parking",
  total_floor_area: "floor_area",
  gross_floor_area: "gross_floor",
  // Total Units reads the row's total_units, as Market Strata does.
  utc_units: "total_units",
};

// Ordering the table falls back to when the user hasn't clicked a header.
// Reproduces what fetchData() does client-side today: active projects first,
// "deferred" statuses (In Planning / Selling Soon / Leasing Soon) beneath them.
// Without this the first page interleaves planning rows with active ones.
export const DEFAULT_SORT = [
  { field: "deferred_status", dir: "asc", emptiesLast: true },
  { field: "name", dir: "asc", emptiesLast: true },
];

// The Project Report's own fallback ordering: the nearest Sales Start Date at
// the top, i.e. most recent first. Rows with no start date (and "TBD") sink to
// the bottom under the emptiesLast rule, where the project name settles their
// order so page boundaries stay put across requests.
export const PROJECT_DEFAULT_SORT = [
  { field: "sales_start_date", dir: "desc", emptiesLast: true },
  { field: "name", dir: "asc", emptiesLast: true },
];

// Unpack the flat filter array into one bucket per filter group. The prefix
// chain is first-match-wins, mirroring the original if/else ladder exactly.
export const decomposeFilterArray = (filterArray) => {
  const out = {
    prt: [],
    pdt: [],
    pt: [],
    ut: [],
    rz: [],
    bp: [],
    dp: [],
    cs: [],
    msr: [],
    ssd: [],
    sod: [],
    ccd: [],
  };

  (filterArray || []).forEach((val) => {
    if (typeof val !== "string") return;
    if (val.startsWith("prt_")) out.prt.push(val.replace("prt_", ""));
    // "ut_cat_*" are structural category headers, never selected as values.
    else if (val.startsWith("ut_")) out.ut.push(val.replace("ut_", ""));
    else if (val.startsWith("pdt_")) out.pdt.push(val.replace("pdt_", ""));
    else if (val.startsWith("pt_")) out.pt.push(val.replace("pt_", ""));
    else if (val.startsWith("rz_")) out.rz.push(val.replace("rz_", ""));
    else if (val.startsWith("bp_")) out.bp.push(val.replace("bp_", ""));
    else if (val.startsWith("dp_")) out.dp.push(val.replace("dp_", ""));
    else if (val.startsWith("cs_")) out.cs.push(val.replace("cs_", ""));
    else if (val.startsWith("msr_")) {
      // A status leaf can bundle a For-Sale status with its Rental equivalent
      // ("Selling Soon / Leasing Soon"); split on "/" so each half matches its
      // own rows by text alone (segment = null → either segment). Legacy ids
      // from older saved views carry a "#strata"/"#rental" marker that scopes
      // the status to one segment, and still resolve.
      val
        .replace("msr_", "")
        .split("/")
        .forEach((chunk) => {
          const part = chunk.trim();
          if (!part) return;
          const hashIdx = part.indexOf("#");
          const marker =
            hashIdx === -1 ? "" : part.slice(hashIdx + 1).toLowerCase();
          const status = (
            hashIdx === -1 ? part : part.slice(0, hashIdx)
          ).toLowerCase();
          let segment = null;
          if (marker === "rental") segment = "Rental";
          else if (marker === "strata") segment = "Strata";
          out.msr.push({ status, segment });
        });
    } else if (val.startsWith("ssd_")) out.ssd.push(val.replace("ssd_", ""));
    else if (val.startsWith("sod_")) out.sod.push(val.replace("sod_", ""));
    else if (val.startsWith("ccd_")) out.ccd.push(val.replace("ccd_", ""));
  });

  return out;
};

// A row's General Unit Type (`type`) as the list it actually is. The backend
// builds it by joining every general unit type on the UTC — "Market Strata,
// Below-Market Rental" (end_points.inc, get_all_child_terms + implode) — so a
// mixed-tenure project carries several values in one string.
export const unitTypeList = (rowType) =>
  String(rowType == null ? "" : rowType)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

// Whether a row carries any of the selected Unit Types.
//
// Membership in that list, not equality with the whole join: comparing the
// joined string as a single value meant a UTC tagged both Market Strata and
// Fee-Simple matched neither leaf, so any project with more than one general
// unit type was invisible to the filter. Shared with the server, which applies
// the same test in presaledb_mo_matches().
export const matchesUnitType = (rowType, selected) => {
  if (!selected || !selected.length) return true;
  const have = unitTypeList(rowType);
  return selected.some((want) => have.includes(want));
};

// Range filters the user has actually set, as { field: { min, max, type } }.
//
// Note the exclusion rule this carries: when a range is active, rows whose value
// is empty / "-" / "zero" / 0 are dropped rather than passed through. The
// client-side predicate does this (buildFilterPredicate) and the server must
// too, or an active range will return more rows than it does today.
const NO_MATCH_UID = -1;

export const activeRanges = (rangeFilters) => {
  const out = {};
  Object.entries(rangeFilters || {}).forEach(([field, f]) => {
    if (!f) return;
    const hasMin = f.min !== null && f.min !== undefined && f.min !== "";
    const hasMax = f.max !== null && f.max !== undefined && f.max !== "";
    if (!hasMin && !hasMax) return;
    out[field] = {
      type: f.type === "date" ? "date" : "number",
      min: hasMin ? f.min : null,
      max: hasMax ? f.max : null,
      excludeEmpty: true,
    };
  });
  return out;
};

// The `filters` half of the request body. Only keys the user has actually set
// are emitted, so an unfiltered request stays small and the server can treat a
// missing key as "no constraint".
//
// NO_MATCH_UID stands in for "match nothing" on the `uids` key, which a missing
// or empty list cannot say. Numeric and negative so the server's loose
// in_array() compare can't equal a real uid — a non-numeric sentinel would
// compare equal to uid 0 under PHP's string-to-number coercion.
//
// `cities` / `neighbours` are the display-label Sets resolveCityFilter() returns,
// not taxonomy keys. These filters are applied per row server-side
// (presaledb_mo_matches over the derived row array), and a row carries its city
// as text in `city` / `neighbour` — the same fields the client-side predicate
// compares, so both paths match on the same values. Taxonomy keys are the scope
// query's currency (`city[]`), not this payload's.
export const buildFilterPayload = ({
  search = "",
  segment = "all",
  filterArray = [],
  cities = null,
  neighbours = null,
  rangeFilters = {},
  selectedUids = [],
  filterToSelected = false,
  priceDateFrom = null,
  areaUids = null,
} = {}) => {
  const f = decomposeFilterArray(filterArray);
  const payload = {};
  const put = (key, list) => {
    if (list && list.length) payload[key] = list;
  };

  // Free-text search. Client-side this scans every value on the row object;
  // server-side it must be an explicit column list, so the two will only agree
  // if that list covers the visible columns.
  const q = String(search || "").trim();
  if (q) payload.q = q;

  // "all" means unset. Bucketed from the row's General Unit Type, not from the
  // strata/rental request tag — see segKey() in MarketOverview.vue.
  if (segment && segment !== "all") payload.segment = segment;

  put("status", f.msr);
  put("project_type", f.prt);
  // `pt_` is no longer emitted by the filter tree but is still honoured as its
  // own AND condition, separate from `prt_`. Kept distinct rather than merged so
  // restoring an old saved view filters exactly as it did before.
  put("legacy_project_type", f.pt);
  put("product_type", f.pdt);
  put("unit_type", f.ut);
  put("construction_status", f.cs);
  put("bp", f.bp);
  put("dp", f.dp);
  put("rz", f.rz);

  const cityList = cities ? [...cities] : [];
  const neighbourList = neighbours ? [...neighbours] : [];
  put("cities", cityList);
  put("neighbours", neighbourList);
  if (cityList.length || neighbourList.length) {
    payload.location_match = LOCATION_MATCH;
  }

  const quarters = {};
  if (f.ssd.length) quarters.sales_start_date = f.ssd;
  if (f.sod.length) quarters.sold_out_date = f.sod;
  if (f.ccd.length) quarters.construction_complete_date = f.ccd;
  if (Object.keys(quarters).length) payload.quarters = quarters;

  const ranges = activeRanges(rangeFilters);
  if (Object.keys(ranges).length) payload.ranges = ranges;

  // Two things narrow the request to an explicit uid list: "Filter to selected"
  // (the rows the user ticked) and a drawn map area, which the endpoint has no
  // geometry for and so receives already resolved to uids. The payload carries
  // one `uids` key, so with both on it carries their intersection.
  //
  // An empty list is "no constraint" server-side (presaledb_mo_matches), so an
  // area that contains nothing sends a uid that cannot exist instead — otherwise
  // "nothing is inside the shape" would read as "everything matches".
  const uidLists = [];
  if (filterToSelected && selectedUids && selectedUids.length) {
    uidLists.push(selectedUids);
  }
  if (areaUids) uidLists.push(areaUids);
  if (uidLists.length === 1) {
    payload.uids = [...uidLists[0]];
  } else if (uidLists.length > 1) {
    const keep = new Set(uidLists[1].map(String));
    payload.uids = uidLists[0].filter((uid) => keep.has(String(uid)));
  }
  if (payload.uids && !payload.uids.length) payload.uids = [NO_MATCH_UID];

  // Price Date window, as the unix second it starts at. The server compares it
  // against each row's `last_price_date` (presaledb_mo_last_price_dates), which
  // is the same field and the same comparison the client-side predicate uses —
  // sent as a resolved bound rather than a day count so both paths window from
  // one instant instead of each taking its own "now".
  if (priceDateFrom) payload.price_date = { from: priceDateFrom };

  if (payload.status) payload.exact_status_match = EXACT_STATUS_MATCH;

  return payload;
};

// Tabulator's sorter list → the `sort` half of the body. `emptiesLast` carries
// the rule wrapSorter() applies client-side: empty values (null, "", "-",
// "zero", 0) sink to the bottom in BOTH directions, and on date columns "TBD"
// sits just above them. A plain ORDER BY will not reproduce that.
export const buildSortPayload = (sorters, fallback = DEFAULT_SORT) => {
  const list = (sorters || [])
    .filter((s) => s && s.field)
    .map((s) => ({
      field: SORT_FIELD_ALIASES[s.field] || s.field,
      dir: s.dir === "desc" ? "desc" : "asc",
      emptiesLast: true,
    }));
  return list.length ? list : fallback;
};

// Full request body. `scope` (city/type/unittype/status/developer/date window)
// travels as the query string instead — it is the dataset the URL asked for and
// the backend already parses it in that form.
export const buildQueryPayload = ({
  filters = {},
  sort = [],
  page = 1,
  limit = 15,
} = {}) => ({
  filters,
  sort: sort && sort.length ? sort : DEFAULT_SORT,
  page: Math.max(1, parseInt(page, 10) || 1),
  limit: Math.max(1, parseInt(limit, 10) || 15),
});

// The endpoint can print a bare access marker (`{"access":false}`) ahead of the
// real envelope, which leaves the body invalid JSON — axios then hands back the
// raw string instead of an object. Recover the envelope from it rather than
// reading the response as an empty result set.
const parseEnvelopeString = (text) => {
  try {
    return JSON.parse(text);
  } catch (e) {
    // The envelope is emitted after any prefix, so take the last one.
    const start = text.lastIndexOf('{"rows"');
    if (start !== -1) {
      try {
        return JSON.parse(text.slice(start));
      } catch (inner) {
        // fall through to the failure below
      }
    }
    console.error("Market Overview: could not parse the paged response body.");
    return null;
  }
};

// Normalize the response envelope. Tolerates a few shapes so a stub backend (or
// a plain array from the existing unpaginated endpoint) still renders.
export const normalizePagedResponse = (raw, { page = 1, limit = 15 } = {}) => {
  const data = typeof raw === "string" ? parseEnvelopeString(raw) : raw;
  if (Array.isArray(data)) {
    // Unpaginated fallback: treat the whole array as one page.
    return {
      rows: data,
      total: data.length,
      lastPage: 1,
      aggregates: null,
      cityBreakdown: [],
    };
  }
  const body = data || {};
  const rows = Array.isArray(body.rows)
    ? body.rows
    : Array.isArray(body.data)
      ? body.data
      : [];
  const total = Number(
    body.total != null ? body.total : body.last_row != null ? body.last_row : rows.length
  ) || 0;
  const lastPage = Number(
    body.lastPage != null
      ? body.lastPage
      : body.last_page != null
        ? body.last_page
        : Math.max(1, Math.ceil(total / Math.max(1, limit)))
  ) || 1;
  return {
    rows,
    total,
    lastPage,
    page: Number(body.page || page) || page,
    aggregates: body.aggregates || null,
    cityBreakdown: body.city_breakdown || body.cityBreakdown || [],
  };
};
